/**
 * TanStack Query hooks for study data fetching
 *
 * Centralizes all study-related API calls with automatic:
 * - Request deduplication (no more manual promise caching)
 * - Stale-while-revalidate caching
 * - Loading/error states
 * - Request cancellation on unmount
 */

import { useQuery, useQueries, UseQueryResult, keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";
import axios from "axios";
import { getStudyDataBaseUrlWithToken, getFeedbackBaseUrl, getApiBaseUrl } from "../config/api";
import { detectDeviceType } from "../config/operatingPoints";
import { isModelRunning } from "../utils/inferenceStatus";
import { readerCaseId } from "../utils/readerCaseId";
import type { NativePredictions } from "../types/predictions";
import { isNeuroModel, type NeuroReview } from "@/types/neuroReview";
import type { TriageReview } from "@/types/triageReview";
import type { AdjudicationVerdict } from "./useSurfacerCandidates";
import { FeedbackRecord } from "../components/types";
import {
  fetchStudyMetadata as fetchStudyMetadataAPI,
  fetchStudyImages,
} from "../utils/dicomMetadataLoader";
import { comparePriority } from "../utils/findingPriority";
import { registerCdnToken } from "../utils/cdnTokens";

export type { StudyMetadataResponse } from "../utils/dicomMetadataLoader";

// ============================================================================
// Types
// ============================================================================

export interface StudyListItem {
  studyId: string;
  isLoaded: boolean;
}

export interface StudyInfoResponse {
  studyId: string;
  isLoading: boolean;
  base_model_prediction: NativePredictions;
  experimental_model_prediction: NativePredictions;
  selectedSeriesId?: string; // undefined when null from backend
  hasValidSeries: boolean;
  prelim: any | null;
  prelim_feedback: any[] | null;
  report: string;
  metadata: Record<string, any>;
  is_report_signed: boolean;
  hasDraft: boolean;
  device_id: string;
}

// Response from /api/get_model_prediction - immutable inference data (cacheable)
export interface ModelPredictionResponse {
  neuro_review?: NeuroReview;
  triage_review?: TriageReview;
  base_model_prediction: NativePredictions;
  experimental_model_prediction: NativePredictions;
  selectedSeriesId: string | null;
  accessionNumber: string | null;
  metadata: Record<string, any>;
  device_id: string;
}

// Response from /api/get_report_state - mutable user state (do NOT cache)
export interface ReportStateResponse {
  report_revision?: string;
  report: string;
  prelim: any | null;
  prelim_feedback: any[] | null;
  prelim_editor_state: any;
  structured_editor_state: any;
  report_exam_type: string | null;
  structured_report: any | null; // Editor-agnostic structured report from backend
  is_report_signed: boolean;
  final_report_html: string | null;
  final_report_text: string | null;
}

// ============================================================================
// Query Keys - Centralized for consistency and cache invalidation
// ============================================================================

export const studyKeys = {
  all: ["studies"] as const,

  details: () => [...studyKeys.all, "detail"] as const,
  detail: (userId: string, studyId: string) => [...studyKeys.details(), userId, studyId] as const,

  // Model prediction - immutable inference data (cacheable with long staleTime)
  modelPrediction: () => [...studyKeys.all, "modelPrediction"] as const,
  studyModelPrediction: (userId: string, studyId: string) =>
    [...studyKeys.modelPrediction(), userId, studyId] as const,

  // Report state - mutable user state (do NOT cache - always refetch)
  reportState: () => [...studyKeys.all, "reportState"] as const,
  studyReportState: (userId: string, studyId: string, readerScheduleId?: string) =>
    [
      ...studyKeys.reportState(),
      userId,
      studyId,
      ...(readerScheduleId ? [readerScheduleId] : []),
    ] as const,

  feedback: () => [...studyKeys.all, "feedback"] as const,
  studyFeedback: (userId: string, studyId: string) =>
    [...studyKeys.feedback(), userId, studyId] as const,

  batch: () => [...studyKeys.all, "batch"] as const,
  batchFeedback: (userId: string, studyIds: string[]) =>
    [...studyKeys.batch(), "feedback", userId, studyIds.sort().join(",")] as const,

  studyMetadata: () => [...studyKeys.all, "studyMetadata"] as const,
  studyMetadataDetail: (userId: string, studyId: string, imagesUrl?: string) =>
    [...studyKeys.studyMetadata(), userId, studyId, ...(imagesUrl ? [{ imagesUrl }] : [])] as const,

  prelim: () => [...studyKeys.all, "prelim"] as const,
  generatePrelim: (userId: string, studyId: string) =>
    [...studyKeys.prelim(), "generate", userId, studyId] as const,

  // Cohorts
  cohorts: () => [...studyKeys.all, "cohorts"] as const,
  readerCohort: (cohortId: string) => [...studyKeys.all, "readerCohort", cohortId] as const,
  reviewCohort: (cohortId: string, reviewer: string) =>
    [...studyKeys.all, "reviewCohort", cohortId, reviewer] as const,
};

// ============================================================================
// API Functions (pure functions, no hooks)
// ============================================================================

/**
 * Fetch model prediction (immutable inference data - cacheable)
 * Uses /get_model_prediction endpoint
 */
export async function fetchModelPrediction(
  token: string,
  userId: string,
  studyId: string,
  signal?: AbortSignal,
): Promise<ModelPredictionResponse | null> {
  const response = await axios.post(
    `${getStudyDataBaseUrlWithToken(token)}/get_model_prediction`,
    {
      user_id: userId,
      study_id: studyId,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal,
    },
  );

  const data = response.data;
  if (data.status && data.status !== 200) {
    return null;
  }

  const metadata = data.metadata || {};
  // Backend already filters predictions to passing conditions
  // No need to filter again on frontend
  const predictions = data.base_model_prediction || {};
  const accessionNumber =
    typeof data.accession_number === "string" && data.accession_number.trim()
      ? data.accession_number.trim()
      : null;

  return {
    base_model_prediction: predictions,
    experimental_model_prediction: predictions, // Use same predictions for both
    selectedSeriesId: data.selected_series_id,
    accessionNumber,
    metadata: Object.entries(metadata).reduce(
      (acc, [seriesId, meta]) => ({
        ...acc,
        [seriesId]: typeof meta === "object" ? meta : {},
      }),
      {},
    ),
    device_id: data.device_id,
    ...(data.neuro_review ? { neuro_review: data.neuro_review } : {}),
    ...(data.triage_review ? { triage_review: data.triage_review } : {}),
  };
}

/**
 * Fetch report state (mutable user state - do NOT cache)
 * Uses /get_report_state endpoint
 */
export async function fetchReportState(
  token: string,
  userId: string,
  studyId: string,
  signal?: AbortSignal,
  readerScheduleId?: string,
): Promise<ReportStateResponse | null> {
  const response = await axios.post(
    `${getStudyDataBaseUrlWithToken(token)}/get_report_state`,
    {
      user_id: userId,
      study_id: studyId,
      ...(readerScheduleId ? { reader_schedule_id: readerScheduleId } : {}),
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      signal,
    },
  );

  const data = response.data;
  if (
    readerScheduleId &&
    (data.reader_schedule_id !== readerScheduleId ||
      typeof data.report_revision !== "string" ||
      typeof data.is_report_signed !== "boolean" ||
      (data.is_report_signed && !data.structured_editor_state))
  ) {
    throw new Error("Reader report storage is unavailable. Reload after the service is updated.");
  }
  if (data.status && data.status !== 200) {
    return null;
  }

  return {
    report_revision: data.report_revision,
    report: data.report || "",
    prelim: data.prelim || null,
    prelim_feedback: data.prelim_feedback || null,
    prelim_editor_state: data.prelim_editor_state,
    structured_editor_state: data.structured_editor_state,
    report_exam_type: data.report_exam_type ?? null,
    structured_report: data.structured_report || null,
    is_report_signed: data.is_report_signed ?? false,
    final_report_html: data.final_report_html ?? null,
    final_report_text: data.final_report_text ?? null,
  };
}

/**
 * Fetch feedback for a single study
 */
async function fetchStudyFeedback(
  token: string,
  userId: string,
  studyId: string,
): Promise<FeedbackRecord | null> {
  const response = await axios.post(
    `${getFeedbackBaseUrl()}/get_feedback`,
    {
      study_id: studyId,
      user_id: userId,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data.feedback || null;
}

/**
 * Batch fetch feedback for multiple studies
 */
async function fetchBatchFeedback(
  token: string,
  userId: string,
  studyIds: string[],
): Promise<Record<string, FeedbackRecord>> {
  if (studyIds.length === 0) return {};

  const response = await axios.post(
    `${getStudyDataBaseUrlWithToken(token)}/batch_feedback`,
    {
      user_id: userId,
      study_ids: studyIds,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data.feedback || {};
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch model prediction only (immutable inference data - cacheable)
 * Uses /get_model_prediction endpoint
 * Use this when you only need predictions and can use long cache times
 */
export function useModelPrediction(
  token: string | null,
  userId: string | null,
  studyId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: studyKeys.studyModelPrediction(userId || "", studyId || ""),
    queryFn: ({ signal }) => fetchModelPrediction(token!, userId!, studyId!, signal),
    enabled: !!token && !!userId && !!studyId && (options?.enabled ?? true),
    staleTime: Infinity, // Immutable data - never stale
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
  });
}

/**
 * Fetch report state only (mutable user state - do NOT cache)
 * Uses /get_report_state endpoint
 * Use this when you need the latest report state
 */
export function useReportState(
  token: string | null,
  userId: string | null,
  studyId: string | null,
  options?: { enabled?: boolean; readerScheduleId?: string },
) {
  return useQuery({
    queryKey: studyKeys.studyReportState(userId || "", studyId || "", options?.readerScheduleId),
    queryFn: async ({ signal }) => {
      const state = await fetchReportState(
        token!,
        userId!,
        studyId!,
        signal,
        options?.readerScheduleId,
      );
      // Unavailable storage is a failed read, not a replacement document.
      // Query retains the last successful state when a background read fails.
      if (!state) throw new Error("Report state is unavailable.");
      return state;
    },
    enabled: !!token && !!userId && !!studyId && (options?.enabled ?? true),
    staleTime: options?.readerScheduleId ? Infinity : 0,
    gcTime: options?.readerScheduleId ? 0 : undefined,
  });
}

/**
 * Fetch feedback for a single study
 */
export function useStudyFeedback(
  token: string | null,
  userId: string | null,
  studyId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: studyKeys.studyFeedback(userId || "", studyId || ""),
    queryFn: () => fetchStudyFeedback(token!, userId!, studyId!),
    enabled: !!token && !!userId && !!studyId && (options?.enabled ?? true),
  });
}

/**
 * Batch fetch feedback for multiple studies
 */
export function useBatchFeedback(
  token: string | null,
  userId: string | null,
  studyIds: string[],
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: studyKeys.batchFeedback(userId || "", studyIds),
    queryFn: () => fetchBatchFeedback(token!, userId!, studyIds),
    enabled: !!token && !!userId && studyIds.length > 0 && (options?.enabled ?? true),
  });
}

/**
 * Fetch multiple studies' feedback in parallel
 */
export function useStudiesFeedback(
  token: string | null,
  userId: string | null,
  studyIds: string[],
  options?: { enabled?: boolean },
): UseQueryResult<FeedbackRecord | null>[] {
  return useQueries({
    queries: studyIds.map((studyId) => ({
      queryKey: studyKeys.studyFeedback(userId || "", studyId),
      queryFn: () => fetchStudyFeedback(token!, userId!, studyId),
      enabled: !!token && !!userId && (options?.enabled ?? true),
    })),
  });
}

/**
 * Fetch study metadata for DICOM series/instance info
 * Used by useDicomSeriesLoader for DICOM viewer initialization. With
 * `imagesUrl`, `studyId` is the study UID and the images are read through that
 * route (a device panel's or a joint review's).
 */
export function useStudyMetadata(
  token: string | null,
  userId: string | null,
  studyId: string | null,
  options?: {
    enabled?: boolean;
    retry?: boolean | number | ((failureCount: number, error: Error) => boolean);
    imagesUrl?: string;
  },
) {
  const imagesUrl = options?.imagesUrl;
  return useQuery({
    queryKey: studyKeys.studyMetadataDetail(userId || "", studyId || "", imagesUrl),
    queryFn: () =>
      imagesUrl
        ? fetchStudyImages(imagesUrl, token!)
        : fetchStudyMetadataAPI(userId!, studyId!, token!),
    enabled: !!token && !!userId && !!studyId && (options?.enabled ?? true),
    // Defaults to the global retry policy unless the caller overrides (e.g. the
    // worklist exam column skips retry on the 423 not-canonicalized case).
    ...(options?.retry !== undefined ? { retry: options.retry } : {}),
  });
}

// ============================================================================
// Exported API Functions (for direct use when hooks aren't appropriate)
// ============================================================================

// ============================================================================
// Study Bbox Query (for preview panel)
// ============================================================================

export interface StudyBboxResponse {
  study_id: string;
  predictions: NativePredictions;
  selected_series_id: string | null;
  // Canonical-only fields. Absent when the study isn't canonicalized — the
  // backend returns `{s3_available: false, error: "study_not_canonicalized"}`
  // in that case and the preview panel renders nothing.
  //
  // Asymmetry with /api/get_study_metadata (which returns 423 for the same
  // condition): bbox is best-effort — predictions are still useful in the
  // case list even when DICOM bytes aren't loadable, so we return 200 with
  // the prediction payload and signal "no preview" via s3_available=false.
  // get_study_metadata exists *only* to drive viewport rendering, so the
  // absence of canonical bytes is a hard failure (423 → migration panel).
  s3_available?: boolean;
  error?: string;
  s3_job_id?: string;
  canonical_cdn_base_url?: string; // CloudFront origin; `<base>/<s3_key>` per instance
  canonical_cdn_token?: string; // appended to CDN object URLs by canonicalObjectUrl
  s3_series?: Record<
    string,
    Array<{
      instance_uid: string;
      instance_number: number;
      s3_key: string;
    }>
  >;
  s3_series_metadata?: Record<string, any>;
}

async function fetchStudyBbox(
  token: string,
  userId: string,
  studyId: string,
): Promise<StudyBboxResponse> {
  const response = await axios.post(
    `${getStudyDataBaseUrlWithToken(token)}/get_study_bbox`,
    {
      user_id: userId,
      study_id: studyId,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  const data = response.data;
  registerCdnToken(data.canonical_cdn_token);

  // Backend already filters predictions to passing conditions
  // No need to filter again on frontend

  return data;
}

/**
 * Fetch bbox predictions and series data for preview panel
 */
export function useStudyBbox(
  token: string | null,
  userId: string | null,
  studyId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...studyKeys.detail(userId || "", studyId || ""), "bbox"] as const,
    queryFn: () => fetchStudyBbox(token!, userId!, studyId!),
    enabled: !!token && !!userId && !!studyId && (options?.enabled ?? true),
  });
}

// ============================================================================
// Cohort API Functions and Hooks
// ============================================================================

export interface Cohort {
  run_id: string;
  /**
   * Display-safe label derived by the backend from the cohort prefix
   * (e.g. "htm-…" → "HTM"). Falls back to the first token of run_id when
   * the cohort doesn't follow the 3-letter-prefix convention. See
   * corp-partnerdocs/shared/reference-docs/2026-04-21-validation-inference-to-demo-architecture.md.
   */
  display_name?: string;
  total: number;
  completed: number;
  pending: number;
  failed: number;
  model: string;
  latest_created_at: string | null;
}

async function fetchCohorts(token: string): Promise<Cohort[]> {
  const response = await axios.get(`${getApiBaseUrl()}/api/cohorts`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.cohorts || [];
}

/** Reporter rows expose comparison_counts per OP instead of detected_at_*.
 * Backend's `_worklist_row` returns one shape or the other, never both — see
 * demo-backend/src/reader_endpoints.py:_worklist_row. */
export interface ComparisonCounts {
  agreement: number;
  ai_only: number;
  report_only: number;
  neither: number;
  // Only emitted under the `recommended` key — the subset of ai_only that
  // survives src/surfacer.py:filter_candidates (category + delta-above-threshold
  // filter). Used by the worklist tile so its number matches the SurfacerPanel
  // card count.
  ai_only_billable?: number;
  // Only under `recommended`: entries left out of that bucket because their
  // recommended_threshold was missing/invalid or absent from classification
  // (reader-ingest counts them here instead of guessing an OP).
  unscored?: number;
}

// Cortex operating points ordered strict→loose. The AI's single probability
// cascades down: firing at high_specificity implies firing at every looser OP.
export type OperatingPoint =
  | "high_specificity"
  | "balanced"
  | "sensitivity_biased"
  | "high_sensitivity";

// Reporter-row surfacer entries — the AI cleared the recommended bar (or
// stricter). `advancement` counts how many OP steps stricter than recommended
// the AI actually fired (0..3, with 0 = just cleared recommended).
// `percentile` is cohort-relative rank of `probability` among other flagged
// occurrences of the same finding, computed at GET time.
export interface SurfacerFinding {
  finding: string;
  // The device's clinical_tier (`acute` / `prompt_workup`). Absent when the
  // backend can't know it (a legacy reporter row ingested before the field, on a
  // model with no category config) — the row then shows no tier badge.
  category?: "acute" | "prompt_workup" | null;
  recommended_op: OperatingPoint;
  fired_op: OperatingPoint;
  advancement: number;
  probability: number;
  percentile?: number;
  // Adjudicator down-rank verdict (>= a2z-reporter-v0.7.1); absent on rows
  // derived before the adjudicator. `report_mentions`/`no_report_coverage` are
  // excluded from the worklist missed count + urgency (see reporterMissedCount).
  predicted_verdict?: AdjudicationVerdict | null;
}

export interface ReaderStudyItem {
  study_uid: string;
  job_id: string;
  status: string;
  model: string;
  created_at: string;
  // Source-delivery batch (cal46/emo32/...) and, for failed jobs, the failure
  // reason — both emitted by reader-ingest (web-demo-backend #406). Absent on
  // cohorts/rows that predate the backend change.
  batch?: string;
  inference_message?: string;
  // Source cohort id. Omitted by the single-cohort wire (the cohort lives in
  // `meta.cohort_id`); `mergeReaderCohorts` stamps it per study so a merged
  // multi-cohort view can attribute each study to its true cohort (e.g. for the
  // skip row's audit `cohort_id`) instead of the collection's representative one.
  cohort_id?: string;
  // Released triage device (web-demo-backend reader_endpoints.worklist_row). The row
  // renders from the device's emitted flags (`triage_review`); its detected_at_* lists
  // are derived by research rules and are ignored.
  triage_device?: boolean;
  // Cortex/drafter shape (mutually exclusive with comparison_counts)
  detected_at_high_specificity?: string[];
  detected_at_balanced?: string[];
  detected_at_sensitivity_biased?: string[];
  detected_at_high_sensitivity?: string[];
  detected_at_recommended?: string[];
  // Reporter shape (mutually exclusive with detected_at_*)
  comparison_counts?: Record<string, ComparisonCounts>;
  // Surfacer-billable findings + their OP advancement / probability / cohort
  // percentile. Ordered by advancement desc, then probability desc. Drives
  // the worklist row's lead/supporting names and the chip color/number.
  surfacer_findings?: SurfacerFinding[];
  // Per-study urgency (reporter cohorts only): backend-computed sort key —
  // `urgency_score` is the OP advancement of the most-advanced finding,
  // `urgency_percentile` the cohort percentile of the most-confident one.
  // Drives the worklist's safety-net-first default order.
  urgency_score?: number | null;
  urgency_percentile?: number | null;
  /**
   * `+`-joined SeriesInstanceUIDs the engine actually analysed. A CADe device
   * may concatenate several series into one volume before inference
   * (device-engine inference.py concatenate_series), so a finding's
   * `top_slices` can legitimately span more than one series. This is the
   * authoritative stack composition — never infer it from which series
   * happened to carry a mark. Served by reader_endpoints.py; absent on rows
   * whose ingest predates the field.
   */
  analyzed_series_id?: string;
  /** Count of series the engine rejected (e.g. <10 slices, non-axial). */
  rejected_series_count?: number;
  /** Distinct, sorted rejection reasons. */
  rejected_series_reasons?: string[];
  /** Results were unreadable at ingest — neither "clean" nor a count holds. */
  rejected_series_unknown?: boolean;
}

export interface ReaderCohortMeta {
  cohort_id: string;
  total: number;
  status_counts: Record<string, number>;
  latest_created_at: string | null;
  models: string[];
}

export interface ReaderCohortResponse {
  meta: ReaderCohortMeta;
  studies: ReaderStudyItem[];
}

export async function fetchReaderCohortData(
  token: string,
  cohortId: string,
): Promise<ReaderCohortResponse> {
  const response = await axios.get(
    `${getApiBaseUrl()}/api/reader/cohort/${encodeURIComponent(cohortId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data as ReaderCohortResponse;
}

async function fetchReaderCohort(token: string, cohortId: string): Promise<ReaderCohortResponse> {
  const data = await fetchReaderCohortData(token, cohortId);
  // Validate every model up front: detectDeviceType throws on unknown device,
  // turning a bad reader row into a React Query error instead of a render crash.
  for (const study of data.studies) {
    if (!isNeuroModel(study.model)) detectDeviceType(study.model);
  }
  return data;
}

/** Poll interval (ms) used while a cohort still has a model running. */
export const READER_COHORT_POLL_MS = 8000;

/**
 * True when at least one study in the cohort is still pending/processing. Used
 * to auto-refresh the worklist until every model has finished, then stop — so
 * "Findings pending" rows flip to findings/no-findings on their own without
 * idle polling once the cohort is fully processed.
 */
export function cohortHasRunningStudies(data: ReaderCohortResponse | undefined): boolean {
  return !!data?.studies?.some((study) => isModelRunning(study.status));
}

export function useReaderCohort(
  token: string | null,
  cohortId: string | null,
  pollWhilePending = false,
) {
  return useQuery({
    queryKey: studyKeys.readerCohort(cohortId || ""),
    queryFn: () => fetchReaderCohort(token!, cohortId!),
    enabled: !!token && !!cohortId,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
    // Auto-refresh while any model is still running; stop once all studies are
    // terminal (see cohortHasRunningStudies).
    //
    // `pollWhilePending` keeps polling even when the response has no running
    // studies — specifically the Live Upload window right after a POST, when the
    // brand-new cohort hasn't been ingested into a2z-reader-derived yet and the
    // endpoint returns 404 (no data → no "running" study). Without this the poll
    // would stop on the first 404 and never discover the study once it lands.
    refetchInterval: (query) =>
      pollWhilePending || cohortHasRunningStudies(query.state.data) ? READER_COHORT_POLL_MS : false,
  });
}

/**
 * Merge several reader-cohort responses into one `{ meta, studies }` object so
 * a multi-cohort collection resolves its `study_uids` across every cohort it
 * spans. Pure (no hooks/network) — the testable seam.
 *
 * Studies are concatenated in cohort order and deduped by the same
 * `study_uid::job_id` composite the worklist uses as a row key (a study should
 * never appear in two distinct cohorts, but a dedupe keeps the merge total
 * honest if it does). The merged `meta` reports the combined total and the
 * primary cohort id; downstream worklist code derives all counts from the
 * `studies` array, not from `meta`, so only these fields need to be sound.
 */
export function mergeReaderCohorts(responses: ReaderCohortResponse[]): ReaderCohortResponse {
  const studies: ReaderStudyItem[] = [];
  const seen = new Set<string>();
  for (const response of responses) {
    for (const study of response.studies) {
      const key = readerCaseId(study);
      if (seen.has(key)) continue;
      seen.add(key);
      // Stamp the source cohort so downstream per-study writes (e.g. skip) record
      // the study's true cohort, not the merged meta's representative one.
      studies.push({ ...study, cohort_id: response.meta.cohort_id });
    }
  }

  const models = Array.from(new Set(responses.flatMap((r) => r.meta.models)));
  const statusCounts: Record<string, number> = {};
  for (const response of responses) {
    for (const [status, count] of Object.entries(response.meta.status_counts)) {
      statusCounts[status] = (statusCounts[status] ?? 0) + count;
    }
  }

  return {
    meta: {
      // Primary cohort id (first listed) — matches the worklist's selectedCohortId.
      cohort_id: responses[0]?.meta.cohort_id ?? "",
      total: studies.length,
      status_counts: statusCounts,
      latest_created_at:
        responses
          .map((r) => r.meta.latest_created_at)
          .filter((d): d is string => d != null)
          .sort()
          .at(-1) ?? null,
      models,
    },
    studies,
  };
}

/**
 * Multi-cohort reader path: load every cohort a collection spans (sharing the
 * single-cohort `useReaderCohort` query key, so caches are shared and the
 * primary cohort isn't fetched twice) and merge them into one reader response.
 *
 * Normalized to the `{ data, isLoading, isError, refetch }` shape of
 * `useReaderCohort` so the worklist swaps in this source with no consumer
 * changes. `useQueries` is called unconditionally — pass `[]` to disable.
 */
export function useReaderCohorts(token: string | null, cohortIds: string[]) {
  const results = useQueries({
    queries: cohortIds.map((cohortId) => ({
      queryKey: studyKeys.readerCohort(cohortId),
      queryFn: () => fetchReaderCohort(token!, cohortId),
      enabled: !!token && !!cohortId,
      staleTime: 60 * 1000,
      placeholderData: keepPreviousData,
    })),
  });

  const allLoaded = results.length > 0 && results.every((r) => r.data != null);
  // A single, fixed-length signature dep — `cohortIds.length` varies between
  // renders (3 deps for May 2026, 0 when the multi-cohort path is off), and a
  // variable-length deps array violates the rules of hooks. `dataUpdatedAt`
  // changes whenever any sub-query's data is refetched, so the merge recomputes
  // exactly when its inputs change.
  const dataSignature = results.map((r) => r.dataUpdatedAt).join(",");
  const data = useMemo(
    () => (allLoaded ? mergeReaderCohorts(results.map((r) => r.data!)) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLoaded, dataSignature],
  );

  return {
    data,
    isLoading: results.some((r) => r.isLoading),
    isError: results.some((r) => r.isError),
    refetch: () => results.forEach((r) => r.refetch()),
  };
}

// ============================================================================
// Reviewer cohort — reader worklist with caller's dispositions overlaid
// ============================================================================

export type DispositionVerdict = "right" | "wrong" | "borderline";

export interface DispositionOverlay {
  verdict: DispositionVerdict;
  comment: string | null;
  updated_at: string;
}

export interface ReviewerStudyItem extends ReaderStudyItem {
  disposition: DispositionOverlay | null;
}

export interface ReviewerCohortMeta extends ReaderCohortMeta {
  caller_reviewed: number;
}

export interface ReviewerCohortResponse {
  meta: ReviewerCohortMeta;
  studies: ReviewerStudyItem[];
}

async function fetchReviewCohort(token: string, cohortId: string): Promise<ReviewerCohortResponse> {
  const response = await axios.get(
    `${getApiBaseUrl()}/api/review/cohort/${encodeURIComponent(cohortId)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const data = response.data as ReviewerCohortResponse;
  for (const study of data.studies) {
    if (!isNeuroModel(study.model)) detectDeviceType(study.model);
  }
  return data;
}

// `reviewerKey` is included in the queryKey to scope the cached cohort to
// the calling user — otherwise an admin who `Assume`s a reviewer would see
// the admin's prior cache poison the impersonated session's disposition view.
export function useReviewCohort(
  token: string | null,
  cohortId: string | null,
  reviewerKey: string | null,
) {
  return useQuery({
    queryKey: studyKeys.reviewCohort(cohortId || "", reviewerKey || ""),
    queryFn: () => fetchReviewCohort(token!, cohortId!),
    enabled: !!token && !!cohortId,
    // Dispositions change on every disposition click, but the click path
    // already calls invalidateQueries(studyKeys.reviewCohort) — so a small
    // staleTime avoids refocus flicker without breaking write-then-read.
    staleTime: 5_000,
    placeholderData: keepPreviousData,
  });
}

export interface SubmitDispositionArgs {
  cohortId: string;
  studyUid: string;
  verdict: DispositionVerdict;
  comment?: string | null;
}

export async function submitDisposition(token: string, args: SubmitDispositionArgs): Promise<void> {
  await axios.post(
    `${getApiBaseUrl()}/api/review/cohort/${encodeURIComponent(args.cohortId)}/study/${encodeURIComponent(args.studyUid)}/disposition`,
    { verdict: args.verdict, comment: args.comment ?? null },
    { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } },
  );
}

export function useCohorts(token: string | null) {
  return useQuery({
    queryKey: studyKeys.cohorts(),
    queryFn: () => fetchCohorts(token!),
    enabled: !!token,
    staleTime: 60 * 1000, // 1 minute
  });
}

// ============================================================================
// Saved Cases (Bookmarks)
// ============================================================================

async function fetchSavedCases(token: string): Promise<string[]> {
  const response = await axios.get(`${getApiBaseUrl()}/api/saved_cases`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.saved_study_ids || [];
}

async function saveCase(token: string, studyId: string): Promise<void> {
  await axios.post(
    `${getApiBaseUrl()}/api/saved_cases`,
    { study_id: studyId },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
}

async function deleteSavedCase(token: string, studyId: string): Promise<void> {
  await axios.delete(`${getApiBaseUrl()}/api/saved_cases`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    data: { study_id: studyId },
  });
}

export const savedCasesKeys = {
  all: ["savedCases"] as const,
  list: () => [...savedCasesKeys.all, "list"] as const,
};

export function useSavedCases(token: string | null) {
  return useQuery({
    queryKey: savedCasesKeys.list(),
    queryFn: () => fetchSavedCases(token!),
    enabled: !!token,
    staleTime: 30 * 1000,
  });
}

export { fetchStudyFeedback, fetchBatchFeedback, saveCase, deleteSavedCase };

// ============================================================================
// Gallery selector (pure — no hook, no network)
// ============================================================================

export interface GalleryFinding {
  condition: string;
  category: string | null;
  topSlice: string | null;
  advancement: number | null;
  percentile: number | null;
}

/**
 * Derive a prediction's PARENT finding key from its (possibly sub-path) key.
 *
 * `get_study_bbox` predictions are keyed by FULL sub-paths like
 * `organ/finding/laterality/midline` or `organ/finding/severity/mild`, whereas
 * `detected_at_recommended` carries 2-segment PARENT keys (`organ/finding`).
 * The safe, data-confirmed rule is: parent = the first two segments joined.
 * 1-segment keys pass through unchanged.
 */
export function parentFindingKey(condition: string): string {
  const segments = condition.split("/");
  return segments.length <= 2 ? condition : segments.slice(0, 2).join("/");
}

/**
 * Derive the ordered, capped set of findings for the worklist preview gallery.
 *
 * Contract: build ONE gallery finding per DETECTED parent key (the entries in
 * `passing` = `detected_at_recommended`), NOT per raw prediction. Each detected
 * parent is joined to a representative `get_study_bbox` prediction (matched on
 * `parentFindingKey`) to pull a `topSlice` and `category`.
 *
 * Ordering: sort by clinical priority via `comparePriority`. Findings with a
 * recognized `clinical_tier` category sort first (acute → … → noted); findings
 * whose category is null or unrecognized sink to the end (rank -1) in
 * `detected_at_recommended` order. Nothing is DROPPED for lacking a category —
 * that was the #489 regression, where soma's kidney/bladder findings (category-
 * null against the old cortex vocabulary) were filtered out of the preview even
 * though they were detected. A detected finding is genuinely detected; we show
 * it, ranked if we can, appended if we can't.
 *
 * A detected finding with no matching prediction still produces a tile (with
 * `topSlice: null`) — detected findings must not vanish because bbox lacks a
 * slice for them.
 */
export function selectGalleryFindings(
  passing: Set<string>, // PARENT condition keys detected at recommended threshold
  predictions: Record<string, any>, // get_study_bbox predictions, keyed by sub-path
): { findings: GalleryFinding[] } {
  // One pass over predictions → parentKey → representative prediction. Prefer an
  // entry that already has a top_slice so a finding's tile gets an image when
  // any of its sub-path predictions has one.
  const repByParent = new Map<string, any>();
  for (const [predKey, p] of Object.entries(predictions)) {
    const parent = parentFindingKey(predKey);
    const existing = repByParent.get(parent);
    const hasSlice = ((p?.top_slices as string[] | undefined)?.length ?? 0) > 0;
    const existingHasSlice = ((existing?.top_slices as string[] | undefined)?.length ?? 0) > 0;
    if (!existing || (!existingHasSlice && hasSlice)) {
      repByParent.set(parent, p);
    }
  }

  // One gallery finding per detected parent, in detected order.
  const findings: GalleryFinding[] = Array.from(passing).map((parent) => {
    const p = repByParent.get(parent);
    return {
      condition: parent,
      category: p?.category ?? null,
      topSlice: (p?.top_slices as string[] | undefined)?.[0] ?? null,
      advancement: p?.advancement ?? null,
      percentile: p?.percentile ?? null,
    };
  });

  findings.sort(comparePriority);

  return { findings };
}

/**
 * Explicit, structured shape-validation for the preview gallery. Pure: returns
 * human-readable warning strings (no console, no env gate — the caller does the
 * DEV-gated console.warn). Catches device-engine output-format drift loudly
 * instead of silently rendering an empty gallery.
 */
export function validateGalleryShape(
  passing: Set<string>,
  predictions: Record<string, any>,
): string[] {
  const warnings: string[] = [];
  const predEntries = Object.entries(predictions);
  const predCount = predEntries.length;

  if (passing.size === 0 && predCount > 0) {
    warnings.push(
      "[PreviewGallery] no detected_at_recommended findings for this study; nothing to preview.",
    );
    return warnings;
  }

  if (predCount > 0 && predEntries.every(([, p]) => p?.category == null)) {
    warnings.push(
      `[PreviewGallery] ${predCount} predictions but none carry 'category' — clinical-priority ordering disabled. ` +
        "Backend transform may have dropped it or device-engine output format changed. (selectGalleryFindings)",
    );
  }

  if (passing.size > 0 && predCount > 0) {
    const matchCount = predEntries.filter(([predKey]) =>
      passing.has(parentFindingKey(predKey)),
    ).length;
    if (matchCount === 0) {
      const detectedSample = Array.from(passing).slice(0, 3);
      const predSample = predEntries.slice(0, 3).map(([k]) => k);
      warnings.push(
        `[PreviewGallery] key-shape mismatch: 0 of ${predCount} predictions match any of ${passing.size} detected findings. ` +
          `detected sample: [${detectedSample.join(", ")}]; prediction sample: [${predSample.join(", ")}]`,
      );
    }
  }

  return warnings;
}
